import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { farmerApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const FarmerDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [farmer, setFarmer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFarmerDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch farmer details
        const farmerData = await farmerApi.getFarmer(id);
        console.log('Farmer details:', farmerData);

        // Ensure farmer ID is in UUID format
        const farmerUUID = farmerData.id || farmerData.farmer_id;
        if (!farmerUUID || !/^[0-9a-fA-F-]{36}$/.test(farmerUUID)) {
          throw new Error('Invalid farmer ID format. Expected UUID.');
        }

        console.log('Frontend ID:', id);
        console.log('Extracted Farmer UUID:', farmerUUID);

        // Fetch farmer emissions data using UUID
        const emissionsData = await farmerApi.getFarmerEmissions(farmerUUID);
        console.log('Emissions data:', emissionsData);

        setFarmer({
          ...farmerData,
          emissions: emissionsData
        });
      } catch (error) {
        console.error('Error fetching farmer details:', error);
        setError('Failed to load farmer details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchFarmerDetails();
  }, [id]);

  if (loading) {
    return <div style={styles.loading}>Loading farmer details...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!farmer) {
    return <div style={styles.error}>Farmer not found.</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>
          ← Back to Dashboard
        </button>
        <h1 style={styles.title}>Farmer Details</h1>
      </div>

      <div style={styles.content}>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Basic Information</h2>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.label}>Name:</span>
              <span style={styles.value}>{farmer.name || farmer.farmer_name}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Phone:</span>
              <span style={styles.value}>{farmer.phone || 'Not set'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Email:</span>
              <span style={styles.value}>{farmer.email || 'Not set'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Village:</span>
              <span style={styles.value}>{farmer.village || 'Not set'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>District:</span>
              <span style={styles.value}>{farmer.district || 'Not set'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Location:</span>
              <span style={styles.value}>{farmer.location || 'Not set'}</span>
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Emissions Data</h2>
          {farmer.emissions ? (
            <div style={styles.emissionsGrid}>
              <div style={styles.emissionCard}>
                <h3 style={styles.emissionTitle}>Total CO2 Emissions</h3>
                <p style={styles.emissionValue}>{farmer.emissions.total_emissions || 0} kg CO2e</p>
              </div>
              <div style={styles.emissionCard}>
                <h3 style={styles.emissionTitle}>Last Updated</h3>
                <p style={styles.emissionValue}>
                  {new Date(farmer.emissions.last_updated).toLocaleDateString()}
                </p>
              </div>
            </div>
          ) : (
            <p style={styles.noData}>No emissions data available</p>
          )}
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Additional Information</h2>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.label}>Status:</span>
              <span style={styles.value}>{farmer.status || 'active'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Created Date:</span>
              <span style={styles.value}>
                {new Date(farmer.created_at).toLocaleDateString()}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Last Updated:</span>
              <span style={styles.value}>
                {new Date(farmer.updated_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Display emissions data */}
        {farmer.emissions && (
          <div style={styles.emissionsContainer}>
            <h2 style={styles.sectionTitle}>CO2 Emissions Breakdown</h2>
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <span style={styles.label}>Fertilizer:</span>
                <span style={styles.value}>{farmer.emissions.fertilizer} kg</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.label}>Pesticide:</span>
                <span style={styles.value}>{farmer.emissions.pesticide} kg</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.label}>Energy:</span>
                <span style={styles.value}>{farmer.emissions.energy} kg</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.label}>Irrigation:</span>
                <span style={styles.value}>{farmer.emissions.irrigation} kg</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.label}>Total:</span>
                <span style={styles.value}>{farmer.emissions.total} kg</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    marginBottom: '30px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--primary-color)',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '8px 16px',
    borderRadius: '4px',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)'
    }
  },
  title: {
    fontSize: '24px',
    margin: 0
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  },
  section: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    fontSize: '20px',
    marginBottom: '20px',
    color: 'var(--text-color)'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px'
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  label: {
    color: '#666',
    fontSize: '14px'
  },
  value: {
    fontSize: '16px',
    fontWeight: '500'
  },
  emissionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px'
  },
  emissionCard: {
    backgroundColor: 'var(--background-color)',
    padding: '20px',
    borderRadius: '8px',
    textAlign: 'center'
  },
  emissionTitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '10px'
  },
  emissionValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'var(--primary-color)'
  },
  noData: {
    textAlign: 'center',
    color: '#666',
    padding: '20px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px'
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--danger-color)',
    fontSize: '18px'
  },
  emissionsContainer: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginTop: '30px'
  }
};

export default FarmerDetailsPage;